"use client"

import { useState, useEffect } from "react"
import axios from "../api/axios"
import { toast } from "react-toastify"
import { Lock, Shield, User, Eye, EyeOff } from "lucide-react"

const ChangePassword = () => {
  const [user, setUser] = useState(null)
  const [users, setUsers] = useState([])
  const [activeTab, setActiveTab] = useState("change") // 'change' or 'reset'

  // Change Password State
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Reset Password State
  const [selectedUserId, setSelectedUserId] = useState("")
  const [resetPassword, setResetPassword] = useState("")
  const [resetConfirmPassword, setResetConfirmPassword] = useState("")
  const [resetLoading, setResetLoading] = useState(false)
  const [showResetPassword, setShowResetPassword] = useState(false)
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await axios.get("/api/user", { withCredentials: true })
        setUser(userRes.data)

        // Only fetch users list if user is admin
        if (userRes.data.role_id === 1 || userRes.data.role_id === 2) {
          const usersRes = await axios.get("/api/users_list")
          setUsers(usersRes.data.filter((u) => u.id !== userRes.data.id)) // Exclude current user
        }
      } catch (err) {
        console.error("Error fetching data:", err)
      }
    }

    fetchData()
  }, [])

  const generatePassword = (length = 12) => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~"
    let pass = ""
    for (let i = 0; i < length; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return pass
  }

  const handleGeneratePassword = () => {
    const newPass = generatePassword()
    setResetPassword(newPass)
    setResetConfirmPassword(newPass)
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      await axios.get("/sanctum/csrf-cookie")

      await axios.post("/api/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      })

      toast.success("Password changed successfully!")

      // Clear form
      setCurrentPassword("")
      setNewPassword("")
      setConfirmPassword("")
    } catch (err) {
      console.error(err.response?.data)
      const message =
        err.response?.data?.message ||
        err.response?.data?.errors?.[Object.keys(err.response.data.errors)[0]][0] ||
        "Operation failed."
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async (e) => {
    e.preventDefault()

    if (!selectedUserId) {
      toast.error("Please select a user")
      return
    }

    if (resetPassword !== resetConfirmPassword) {
      toast.error("Passwords do not match")
      return
    }

    setResetLoading(true)

    try {
      await axios.get("/sanctum/csrf-cookie")

      // This would be the API endpoint for admin password reset
      await axios.post("/api/admin/reset-password", {
        user_id: selectedUserId,
        new_password: resetPassword,
        new_password_confirmation: resetConfirmPassword,
      })

      toast.success("Password reset successfully!")

      // Clear form
      setSelectedUserId("")
      setResetPassword("")
      setResetConfirmPassword("")
    } catch (err) {
      console.error(err.response?.data)
      const message =
        err.response?.data?.message ||
        err.response?.data?.errors?.[Object.keys(err.response.data.errors)[0]][0] ||
        "Password reset failed."
      toast.error(message)
    } finally {
      setResetLoading(false)
    }
  }

  const isAdmin = user && (user.role_id === 1 || user.role_id === 2)

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl mb-4">
            <Lock size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Password Management</h1>
          <p className="text-gray-600">
            {isAdmin ? "Change your password or reset passwords for other users" : "Change your account password"}
          </p>
        </div>

        {/* Tabs - Only show if admin */}
        {isAdmin && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-2 mb-8">
            <div className="flex space-x-2">
              <button
                onClick={() => setActiveTab("change")}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-medium transition-colors ${
                  activeTab === "change"
                    ? "bg-orange-100 text-orange-700 border border-orange-200"
                    : "text-gray-600 hover:text-orange-600 hover:bg-orange-50"
                }`}
              >
                <Lock size={20} />
                <span>Change My Password</span>
              </button>
              <button
                onClick={() => setActiveTab("reset")}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-medium transition-colors ${
                  activeTab === "reset"
                    ? "bg-orange-100 text-orange-700 border border-orange-200"
                    : "text-gray-600 hover:text-orange-600 hover:bg-orange-50"
                }`}
              >
                <Shield size={20} />
                <span>Reset User Password</span>
              </button>
            </div>
          </div>
        )}

        {/* Change Password Form */}
        {activeTab === "change" && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="flex items-center space-x-3 mb-6">
              <Lock size={24} className="text-orange-600" />
              <h2 className="text-2xl font-bold text-gray-900">Change Your Password</h2>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-orange-600"
                  >
                    {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-orange-600"
                  >
                    {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-orange-600"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-3 rounded-lg font-semibold transition-all duration-200 ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {loading ? "Changing Password..." : "Change Password"}
              </button>
            </form>
          </div>
        )}

        {/* Reset Password Form - Admin Only */}
        {activeTab === "reset" && isAdmin && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="flex items-center space-x-3 mb-6">
              <Shield size={24} className="text-orange-600" />
              <h2 className="text-2xl font-bold text-gray-900">Reset User Password</h2>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select User</label>
                <div className="relative">
                  <User size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none"
                    required
                  >
                    <option value="">Choose a user to reset password</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.email}) - {user.role?.role_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">New Password</label>
                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                  >
                    Generate Password
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showResetPassword ? "text" : "password"}
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(!showResetPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-orange-600"
                  >
                    {showResetPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                <div className="relative">
                  <input
                    type={showResetConfirmPassword ? "text" : "password"}
                    value={resetConfirmPassword}
                    onChange={(e) => setResetConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetConfirmPassword(!showResetConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-orange-600"
                  >
                    {showResetConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {selectedUserId && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <Shield size={16} className="text-yellow-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Admin Action</p>
                      <p className="text-sm text-yellow-700">
                        You are about to reset the password for{" "}
                        <strong>{users.find((u) => u.id.toString() === selectedUserId)?.name}</strong>. This action
                        cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={resetLoading}
                className={`w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white py-3 rounded-lg font-semibold transition-all duration-200 ${
                  resetLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {resetLoading ? "Resetting Password..." : "Reset User Password"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}

export default ChangePassword
